/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { Fragment, useEffect, useRef, useState } from "react";
import { ImageEditContext } from "cms/images/image-selector/context/image-edit-context";
import { useAuth } from "cms/auth/hooks/use-auth";
import { ImageSelectDialog } from "cms/images/image-selector/dialog-select";
import { ImageEditDialog } from "cms/images/image-selector/dialog-edit";
import { ImageSelectorDisplay } from "cms/images/image-selector/display";
import { ImageDeleteDialog } from "cms/images/image-selector/dialog-delete";
import { uploadFiles } from "cms/images/image-selector/dialog-select/upload/AzureStorage/upload-processor";
import { DefaultAddImageButton } from "cms/images/image-selector/exported/add-image-button";
import { DefaultImageSelectorDisplayComponent } from "cms/images/image-selector/exported/display/display-panel";
import { useImageLoadedForUpload } from "cms/images/hooks/use-image-loaded";
import { useSimplicityImage } from "cms/images/hooks/use-simplicity-image";
import { DEFAULT_UPLOAD_STATUS } from "cms/images/image-selector/image-selector-config";
import { MODES } from "cms/images/image-selector/image-selector-config";
import { startingMode, } from "cms/images/image-selector/image-selector-config";
import { EDIT_TABS } from "cms/images/image-selector/image-selector-config";
import { startingEditTab } from "cms/images/image-selector/image-selector-config";
import { Logger } from "cms/utils/log/logger";
import { isGuid } from "utils/empty-exists";

const ImageSelector = (props) => {

    const AddButtonComponent = props?.AddButtonComponent;   // (optional) a component to show (in page) when no image is selected
    const DisplayComponent = props?.DisplayComponent;       // (optional) a component to show the selected image (in page)
    const _image = props?.image;                            // the source/starting image from the parent
    const showSave = props?.showSave ?? false;              // controls is the save or select button is shown
    const onSaveImageHandler = props?.onSaveImage;          // called on exit when image is saved
    const onSelectImageHandler = props?.onSelectImage;      // called on exit when image is selected
    const onImageRemoved = props?.onImageRemoved;            // called on exit when image is removed
    const onImageDeleted = props?.onImageDeleted;            // called on exit when image is deleted
    const debug = props?.debug;                             // a flag to enable debug information
    const onReload = props?.onReload;
    const setImageHandler = props?.setImage;
    const defaultImageSelectorWidth = props?.defaultImageSelectorWidth;
    const defaultImageSelectorHeight = props?.defaultImageSelectorHeight;

    // NOTE: upload is handled internal to the selector, and the result might be a onSelectImageHandler callback
    if (typeof onSaveImageHandler !== "function") {
        throw Error("onSaveImageHandler was not supplied, or is not a function");
    }

    if (typeof onSelectImageHandler !== "function") {
        throw Error("onSaveImageHandler was not supplied, or is not a function");
    }

    if (typeof onReload !== "function") {
        throw Error("onReload was not supplied, or is not a function");
    }

    const { token } = useAuth();
    const { saveImage } = useSimplicityImage();
    const [image, setImage] = useState(_image);
    const [filesReady, setFilesReady] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadStatus, setUploadStatus] = useState(DEFAULT_UPLOAD_STATUS);
    const [files, setFiles] = useState([]);
    const [fileError, setFileError] = useState("");
    const refs = useRef([]);
    const [images, setImages] = useState(); // for Gallery
    const [original, setOriginal] = useState(); // for Gallery
    const [selectedImage, setSelectedImage] = useState(_image);
    const [open, setOpen] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [openRemove, setOpenRemove] = useState(false);
    const [openArchive, setOpenArchive] = useState(false);
    const [openRename, setOpenRename] = useState(false);
    const [openReplace, setOpenReplace] = useState(false);
    const [hasImage, setHasImage] = useState();
    const initialLocalState = { image: { title: "", description: "" } };
    const [localState, setLocalState] = useState(initialLocalState);
    const [mode, setMode] = useState(startingMode);
    const [selectedTab, setSelectedTab] = useState(0);
    const [selectedEditTab, setSelectedEditTab] = useState(0);
    const [isArchiving, setIsArchiving] = useState(false);
    const [isReplacing, setIsReplacing] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [isFieldValid, setIsFieldValid] = useState(false);
    const { onLoad } = useImageLoadedForUpload(refs, files);
    const statusList = [];
    
    useEffect(() => {
        if (!image || !isGuid(image?.guid)) {
            setMode(MODES.SELECT);
        } else {
            setOriginal(image);
            setSelectedImage(image);
            setHasImage(true);
        }
    }, [image, open]);

    const handleTabsChange = (event, newValue) => {
        // setSelectedTab(newValue)
        switch (newValue) {
            case 0:
                // setSelectedTab(EDIT_TABS.PROPERTIES)
                setSelectedTab(newValue);
                break;
            case 1:
                setSelectedTab(newValue);
                break;
            default:
                break;
        }
    };
    const onChange = async () => {
        setOpen(true);
        setMode(startingMode);
        setSelectedTab(0);
    };
    
    const onEdit = async () => {
        setOpenEdit(true);
    };
    
    const onRemove = async () => {
        setOpenRemove(true);
    };
    
    const onUploadStart = async () => {
        Logger.debug("Start Image Upload...");
        // console.log('Start Image Upload...');
        await handleUpload();
    };
    
    const handleOnImageUploaded = async (uploaded_images) => {
        await onImageUploaded(uploaded_images);
        const firstImage = uploaded_images[0];
        setSelectedImage(firstImage);
    };
    
    const handleUpload = async () => {
        Logger.debug("[image-selector/index.js] handleUpload");
        await uploadFiles(
            setUploading,
            statusList,
            setUploadStatus,
            files,
            handleOnImageUploaded,
            token,
        );
    };
    
    const onImageUploaded = async (results) => {
        Logger.debug("Image Uploaded", results);
        const firstImage = results[0];
        setSelectedImage(firstImage);
        onSelectImage(firstImage);
        setMode(MODES.EDIT);
        setSelectedEditTab(0);
        setOpenEdit(true);
        onReload();
    };
    
    const onImageReplaced = async (image) => {
        Logger.debug("Image Replaced", image);
        setImage(image);
        setSelectedImage(image);
        onSelectImage(image);
        setImageHandler(image); // update wrapper cache
        setMode(MODES.EDIT);
        setOpenReplace(false);
        setOpenEdit(false);
        onReload(image);
        // if (typeof onReload === 'function') {
        //     onReload();
        // }
    };
    
    const onImageArchived = async () => {
        Logger.debug("onImageArchived");
        setImage(undefined);
        setSelectedImage(undefined);
        await onSelectImageHandler(undefined);
        setOpenArchive(false);
        setOpenEdit(false);
        setHasChanges(true);
        onReload();
    };
    
    const clearFiles = async () => {
        setFilesReady(false);
        setFiles([]);
        setFileError("");
    };
    
    const onSaveImage = async () => {
        // is partial record as update
        const image = localState?.image;
        if (image?.guid) {
            const image_response = await saveImage(image);
            onSaveImageHandler(image_response);
        }
        handleEditClose();
        if (showSave) {
            handleClose();
        }
        onReload(image);
    };
    
    const onSelectImage = (selectImage) => {
        Logger.debug("onSelectImage", selectImage);
        setSelectedImage(selectImage);
        onSelectImageHandler(selectImage);
        handleClose();
    };
    
    const handleClose = () => {
        setSelectedImage(original);
        setMode(MODES.SELECT);
        setSelectedTab(0);
        setOpen(false);
        setFileError("");
    };
    
    const handleEditClose = () => {
        setOpenEdit(false);
        setSelectedTab(0);
        setOpenEdit(false);
        setOpen(false);
        setIsArchiving(false);
        setIsReplacing(false);
        setOpenReplace(false);
    };

    const imageEditContext = {
        AddButtonComponent,
        DisplayComponent,
        open,
        setOpen,
        isArchiving,
        setIsArchiving,
        isReplacing,
        setIsReplacing,
        hasChanges,
        setHasChanges,
        openEdit,
        setOpenEdit,
        openReplace,
        setOpenReplace,
        openRemove,
        setOpenRemove,
        openArchive,
        setOpenArchive,
        openRename,
        setOpenRename,
        handleClose,
        handleEditClose,
        image,
        setImage,
        hasImage,
        onChange,
        onEdit,
        onRemove,
        selectedImage,
        setSelectedImage,
        isFieldValid, 
        setIsFieldValid,
        MODES,
        mode,
        setMode,
        startingMode,
        selectedTab,
        setSelectedTab,
        selectedEditTab,
        setSelectedEditTab,
        startingEditTab,
        EDIT_TABS,
        handleTabsChange,
        // Delete Dialog
        // external return calls
        showSave,
        onSaveImage,
        onSelectImage,
        onImageUploaded,
        onImageDeleted,
        onImageRemoved,
        onImageReplaced,
        onImageArchived,
        images,
        setImages,
        localState,
        setLocalState,
        uploadFiles,
        onUploadStart,
        clearFiles,
        filesReady,
        setFilesReady,
        fileError,
        setFileError,
        uploading,
        setUploading,
        uploadStatus,
        setUploadStatus,
        files,
        refs,
        setFiles,
        statusList,
        onLoad,
        debug,
    };

    useEffect(() => {
        if (!image) {
            setMode(MODES.SELECT);
        } else {
            setOriginal(image);
            setSelectedImage(image);
            setHasImage(true);
        }
    }, [image]);

    useEffect(() => {
        if (selectedImage) {
            setHasImage(true);
        }
    }, [selectedImage]);

    useEffect(() => {
        setImage(_image);
        setHasImage(true);
    }, [_image]);

    return (
        <Fragment>
            <ImageEditContext.Provider value={imageEditContext}>
                <ImageSelectorDisplay
                    defaultImageSelectorWidth={defaultImageSelectorWidth}
                    defaultImageSelectorHeight={defaultImageSelectorHeight}
                />
                <ImageSelectDialog />
                <ImageEditDialog />
                <ImageDeleteDialog deleteType="REMOVE" />
            </ImageEditContext.Provider>
        </Fragment>
    );
};

export {
    DefaultImageSelectorDisplayComponent,
    DefaultAddImageButton,
    ImageSelectDialog
};

export { ImageSelector };
